import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Crown, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthShowcase from "@/components/AuthShowcase";

const PERKS = [
  "Unlimited receipt scans every month",
  "AI Schedule C categorization",
  "Month & year-to-date dashboards",
  "CSV, Excel & PDF export for tax time",
  "Exclusive Dark Pro theme",
];

export default function Upgrade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 noise-overlay grid lg:grid-cols-2">
      <div className="relative flex flex-col justify-center px-8 sm:px-16 py-12 overflow-hidden">
        <div className="absolute top-[-8rem] left-1/2 -translate-x-1/2 h-[32rem] w-[40rem] max-w-full bg-[radial-gradient(ellipse_at_top,rgba(212,195,163,0.12),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-md w-full">
          <button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-10 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </button>

          <span className="inline-flex w-fit items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 text-[#D4C3A3] uppercase tracking-[0.2em] mb-5">
            <Crown className="h-3.5 w-3.5" /> 7-day free trial
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none mb-4 text-white">
            Try Pro free.<br />Never dread tax season.
          </h1>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Start a <span className="font-semibold text-white">7-day free trial</span> — unlimited receipts,
            cancel anytime before it ends and you won't be charged. After the trial it's just $6/month.
          </p>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 mb-8">
            <div className="flex items-end gap-1 mb-1">
              <span className="font-display text-5xl font-extrabold tracking-tight text-white font-mono-num">$0</span>
              <span className="text-zinc-500 mb-2">for 7 days</span>
            </div>
            <p className="text-sm text-zinc-500 mb-6">then $6/month · cancel anytime</p>
            <ul className="space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-300">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-[#D4C3A3]" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <Button data-testid="checkout-button" onClick={checkout} disabled={loading} className="w-full h-12 text-base font-semibold rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] hover:scale-[1.01] transition-all duration-200">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</> : "Start 7-day free trial"}
          </Button>
          <p className="text-xs text-zinc-500 text-center mt-3">No charge for 7 days · Secure checkout via Stripe · Cancel anytime</p>
        </div>
      </div>

      <AuthShowcase
        badge="Receipt Republic Pro"
        quote="Built for drivers, freelancers & delivery workers filing Schedule C."
        checks={["Unlimited scans", "Cancel anytime"]}
      />
    </div>
  );
}
